import classNames from "classnames";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { handlingDynamicFontBasedOnLanguage } from "../../../utils/UtilsTypography";
import "./Typography.scss";

enum variants {
  h1,
  h2,
  h3,
  title1,
  title2,
  title3,
  body1,
  body2,
  body3,
  caption,
  flag,
}

enum weights {
  w700,
  w400,
  w500,
  wMedium,
  wBold,
}

interface Props {
  variant: keyof typeof variants;
  weight?: keyof typeof weights;
  component?: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  testId?: string;
}

export const Typography: React.FC<Props> = (props) => {
  const {
    variant,
    children,
    component = "div",
    className,
    style,
    weight = "",
    onClick,
    testId,
  } = props;

  // Get lang attribute from document
  const currentLang =
    typeof document === "undefined" ? "" : document.documentElement.lang;

  // Get i18n instance for Trans component support
  const { i18n } = useTranslation();

  const fontClass = useMemo(
    () => handlingDynamicFontBasedOnLanguage(currentLang, children, i18n),
    [currentLang, children, i18n]
  );

  const wrapClass = classNames(
    "typography",
    { [variant]: !!variant },
    { [weight]: !!weight },
    fontClass,
    className
  );

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const CustomTag = component as keyof JSX.IntrinsicElements;

  return (
    <CustomTag
      tabIndex={-1}
      data-testid={testId}
      className={wrapClass}
      style={style}
      onClick={handleOnClick}
    >
      {children}
    </CustomTag>
  );
};
